import { useEffect, useRef, useState } from "react";
import Product from "../Product/Product";

const FeaturedSection = ({
  products,
  onAddToCart,
  onProductClick,
  heading,
  subHeading,
}) => {
  const listRef = useRef(null);
  const [activePage, setActivePage] = useState(0);
  const [pageCount, setPageCount] = useState(1);

  const canPrev = activePage > 0;
  const canNext = activePage < pageCount - 1;

  const goToPage = (page) => {
    const container = listRef.current;
    if (!container) return;

    const target = page * container.clientWidth;
    container.scrollTo({ left: target, behavior: "smooth" });
  };

  useEffect(() => {
    const container = listRef.current;
    if (!container) return;

    const updatePages = () => {
      const pages = Math.max(
        1,
        Math.ceil(container.scrollWidth / container.clientWidth),
      );
      setPageCount(pages);
      setActivePage(Math.min(activePage, pages - 1));
    };

    updatePages();

    const resizeObserver = new ResizeObserver(updatePages);
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, [products]);

  const onScroll = () => {
    const container = listRef.current;
    if (!container) return;

    const nextPage = Math.round(container.scrollLeft / container.clientWidth);
    setActivePage(nextPage);
  };

  return (
    <section className="mb-20">
      <div className="text-center mb-12">
        <h3 className="text-5xl font-black text-gray-900 mb-4 tracking-tight">
          {heading}
        </h3>
        <p className="text-xl text-gray-600 max-w-2xl mx-auto">{subHeading}</p>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        {pageCount > 1 && (
          <button
            type="button"
            onClick={() => canPrev && goToPage(activePage - 1)}
            disabled={!canPrev}
            aria-label="Previous slide"
            className={`flex h-10 w-10 items-center justify-center mr-3 rounded-full bg-red-600 text-white shadow transition hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 ${
              !canPrev ? "cursor-not-allowed opacity-40" : ""
            }`}
          >
            ‹
          </button>
        )}

        <div className="flex-1 flex flex-col">
          <div
            ref={listRef}
            onScroll={onScroll}
            className="flex gap-6 overflow-x-auto scroll-px-6 snap-x snap-mandatory scroll-smooth pb-4 -mx-4 px-4 hide-scrollbar"
          >
            {products &&
              products.length > 0 &&
              products.map((product) => (
                <div
                  key={product.id}
                  className="snap-start flex-shrink-0 w-full sm:w-1/2 lg:w-1/3"
                >
                  <Product
                    product={product}
                    onAddToCart={onAddToCart}
                    onProductClick={onProductClick}
                  />
                </div>
              ))}
          </div>

          <div className="mt-6 flex justify-center gap-2">
            {Array.from({ length: pageCount }).map((_, idx) => (
              <button
                key={idx}
                type="button"
                className={`h-2 w-2 rounded-full transition-colors ${
                  idx === activePage ? "bg-red-600" : "bg-gray-300"
                }`}
                aria-label={`Go to slide ${idx + 1}`}
                onClick={() => goToPage(idx)}
              />
            ))}
          </div>
        </div>

        {pageCount > 1 && (
          <button
            type="button"
            onClick={() => canNext && goToPage(activePage + 1)}
            disabled={!canNext}
            aria-label="Next slide"
            className={`flex h-10 w-10 items-center justify-center rounded-full ml-3 bg-red-600 text-white shadow transition hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 ${
              !canNext ? "cursor-not-allowed opacity-40" : ""
            }`}
          >
            ›
          </button>
        )}
      </div>
    </section>
  );
};

export default FeaturedSection;
